export const DEMO_DAY = '2026-10-19';
export const DEMO_NOW = '2026-10-19T18:00:00-03:00';
export const RATE_CENTS = 4662;
export const STORAGE_KEY = 'corteva-estadia-demo-v2';
export const RECIPIENTS = (
  process.env.REACT_APP_ESTADIA_FINANCE_RECIPIENTS ||
  'Financeiro Deloitte (destinatários configurados na API)'
)
  .split(',')
  .map((recipient) => recipient.trim())
  .filter(Boolean);
export type Category =
  'Produto acabado' | 'Matéria prima' | 'Embalagem' | 'Não informada';
export type EventKey =
  | 'scheduled'
  | 'arrival'
  | 'unloaded'
  | 'docked'
  | 'loaded'
  | 'released'
  | 'exit';
export type Events = Partial<Record<EventKey, string>> & {
  scheduled: string;
  arrival: string;
};
export type Trip = {
  id: string;
  sourceId?: string;
  sourceExitAt?: string;
  sourceMissing?: string[];
  history?: { at: string; actor: string; message: string }[];
  plate: string;
  carrier: string;
  unit: string;
  circuit: boolean;
  site: 'Fábrica' | 'Fazenda' | 'Não informado';
  cnpj: string;
  address: string;
  category: Category;
  tons: number;
  operation: 'Descarga' | 'Descarga e carregamento';
  events: Events;
  validation: 'pending' | 'validated' | 'rejected';
  validationReason?: string;
  assessment?: EstadiaAssessment;
};
export type EstadiaAssessment = {
  eligible: boolean;
  rec: number;
  exp: number;
  total: number;
  remaining: number;
  excess: number;
  cents: number;
  complete: boolean;
  phase: string;
  status: 'none' | 'pay' | 'risk' | 'ok';
};
export type Invoice = {
  id: string;
  carrier: string;
  tripIds: string[];
  status: 'pending' | 'approved' | 'rejected' | 'reversed';
  totalCents?: number;
  createdAt: string;
  protocol?: string;
  reason?: string;
  history: { at: string; actor: string; message: string }[];
};
export type Delivery = {
  id: string;
  invoiceId: string;
  at: string;
  recipients: string[];
  protocol: string;
  status: 'Registrado localmente' | 'Enviado' | 'Capturado no ambiente local';
};
export type DemoState = {
  version: 2;
  trips: Trip[];
  invoices: Invoice[];
  deliveries: Delivery[];
};
export type Metrics = ReturnType<typeof metrics>;
export const hasTripData = (trip: Trip, field: string) =>
  !trip.sourceMissing?.includes(field);
export const money = (cents: number) =>
  (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
export const duration = (minutes: number) => {
  if (!Number.isFinite(minutes)) return '—';
  const seconds = Math.round(Math.abs(minutes) * 60);
  const suffix = seconds % 60;
  return `${minutes < 0 ? '−' : ''}${String(Math.floor(seconds / 3600)).padStart(2, '0')}:${String(Math.floor(seconds / 60) % 60).padStart(2, '0')}${suffix ? `:${String(suffix).padStart(2, '0')}` : ''}`;
};
export const clock = (value?: string) =>
  value
    ? new Date(value).toLocaleTimeString('pt-BR', {
        timeZone: 'America/Sao_Paulo',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';
export const dateTime = (value?: string) =>
  value
    ? new Date(value).toLocaleString('pt-BR', {
        timeZone: 'America/Sao_Paulo',
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : 'Não informado';
export const sum = (values: number[]) =>
  values.reduce((total, value) => total + value, 0);
const businessDateFormat = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/Sao_Paulo',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});
export const businessDay = (value: string) => {
  if (!Number.isFinite(Date.parse(value))) return '';
  const parts = businessDateFormat.formatToParts(new Date(value));
  return ['year', 'month', 'day']
    .map((type) => parts.find((part) => part.type === type)!.value)
    .join('-');
};
const minutesBetween = (start: string, end: string) =>
  Math.max(0, Math.round((Date.parse(end) - Date.parse(start)) / 60000));

export function metrics(trip: Trip, now = DEMO_NOW): EstadiaAssessment {
  if (trip.assessment) return trip.assessment;
  const { events } = trip;
  const eligible = Date.parse(events.arrival) <= Date.parse(events.scheduled);
  const start = eligible ? events.scheduled : events.arrival;
  const rec = minutesBetween(start, events.unloaded || now);
  const terminal =
    trip.operation === 'Descarga' ? events.released : events.loaded;
  const exp = events.docked
    ? minutesBetween(events.docked, terminal || now)
    : 0;
  const total = rec + exp;
  const remaining = 600 - total;
  const excess = eligible ? Math.max(0, -remaining) : 0;
  const cents = Math.round((excess * RATE_CENTS) / 60);
  const complete = Boolean(events.unloaded && terminal);
  const phase = events.exit
    ? 'Operação encerrada'
    : terminal
      ? 'Liberado / aguardando saída'
      : events.docked
        ? 'Em carregamento'
        : events.unloaded
          ? 'Vazio · aguardando'
          : 'Em recebimento';
  const status = !eligible
    ? 'none'
    : remaining < 0
      ? 'pay'
      : remaining <= 120
        ? 'risk'
        : 'ok';
  return {
    eligible,
    rec,
    exp,
    total,
    remaining,
    excess,
    cents,
    complete,
    phase,
    status,
  };
}

export function hasRecordedExit(trip: Trip) {
  return Boolean(trip.sourceExitAt || trip.events.exit);
}

export function operationalGroups(trips: Trip[]) {
  const active = trips.filter(
    (trip) => !metrics(trip).complete
  );
  const exceeded = active.filter((trip) => {
    const value = metrics(trip);
    return (
      value.cents > 0 ||
      value.excess > 0 ||
      value.total > 600 ||
      value.remaining < 0
    );
  });
  const exceededIds = new Set(exceeded.map((trip) => trip.id));
  const within = active.filter(
    (trip) => metrics(trip).eligible && !exceededIds.has(trip.id)
  );
  const critical = within.filter((trip) => metrics(trip).remaining <= 120);
  return { active, exceeded, within, critical };
}

export const STATUS_LABELS: Record<string, string> = {
  none: 'Sem direito',
  pay: 'Estadia a pagar',
  risk: 'Franquia no limite',
  ok: 'Dentro da franquia',
};
export const INVOICE_LABELS: Record<Invoice['status'], string> = {
  pending: 'Aguardando aprovação',
  approved: 'Aprovada',
  rejected: 'Reprovada',
  reversed: 'Estornada',
};
export function shift(trip: Trip) {
  if (!Number.isFinite(Date.parse(trip.events.scheduled)))
    return 'Não informado';
  const hour =
    Number(
      new Intl.DateTimeFormat('en-GB', {
        timeZone: 'America/Sao_Paulo',
        hour: '2-digit',
        hour12: false,
      }).format(new Date(trip.events.scheduled))
    ) % 24;
  return hour < 6
    ? 'Madrugada'
    : hour < 13
      ? 'Manhã'
      : hour < 18
        ? 'Tarde'
        : 'Noite';
}
export function invoiceTotal(state: DemoState, invoice: Invoice) {
  if (invoice.totalCents !== undefined) return invoice.totalCents;
  return sum(
    state.trips
      .filter((trip) => invoice.tripIds.includes(trip.id))
      .map((trip) => metrics(trip).cents)
  );
}
export function canBill(state: DemoState, trip: Trip) {
  const metric = metrics(trip);
  return (
    metric.complete &&
    metric.cents > 0 &&
    trip.validation === 'validated' &&
    !state.invoices.some(
      (invoice) =>
        invoice.status !== 'reversed' && invoice.tripIds.includes(trip.id)
    )
  );
}
function stamp() {
  return new Date().toISOString();
}
export function createInvoice(state: DemoState, tripIds: string[]): DemoState {
  const selected = state.trips.filter((trip) => tripIds.includes(trip.id));
  if (
    !selected.length ||
    new Set(tripIds).size !== tripIds.length ||
    selected.length !== tripIds.length ||
    selected.some((trip) => !canBill(state, trip))
  )
    throw new Error(
      'Selecione apenas viagens validadas, encerradas e ainda não faturadas.'
    );
  if (new Set(selected.map((trip) => trip.carrier)).size !== 1)
    throw new Error(
      'Uma fatura deve conter viagens de uma única transportadora.'
    );
  const id = `FAT-2026-${String(state.invoices.length + 1).padStart(4, '0')}`;
  return {
    ...state,
    invoices: [
      ...state.invoices,
      {
        id,
        carrier: selected[0].carrier,
        tripIds,
        createdAt: stamp(),
        status: 'pending',
        history: [
          {
            at: stamp(),
            actor: 'Transportador',
            message: 'Fatura consolidada gerada e submetida à Corteva.',
          },
        ],
      },
    ],
  };
}
export function decideInvoice(
  state: DemoState,
  id: string,
  approve: boolean,
  reason = ''
): DemoState {
  const invoice = state.invoices.find((item) => item.id === id);
  if (!invoice || invoice.status !== 'pending')
    throw new Error('Somente faturas pendentes podem ser decididas.');
  if (!approve && !reason.trim())
    throw new Error('Informe o motivo da reprovação.');
  const protocol = approve
    ? `WCK26${String(state.invoices.indexOf(invoice) + 1).padStart(6, '0')}A1`
    : undefined;
  return {
    ...state,
    invoices: state.invoices.map((item) =>
      item.id === id
        ? {
            ...item,
            status: approve ? 'approved' : 'rejected',
            protocol,
            reason: approve ? undefined : reason.trim(),
            history: [
              ...item.history,
              {
                at: stamp(),
                actor: 'Gestor Corteva',
                message: approve
                  ? `Aprovada. Protocolo ${protocol}.`
                  : `Reprovada: ${reason.trim()}`,
              },
            ],
          }
        : item
    ),
  };
}
export function resubmitInvoice(state: DemoState, id: string): DemoState {
  const invoice = state.invoices.find((item) => item.id === id);
  if (!invoice || invoice.status !== 'rejected')
    throw new Error('Somente faturas reprovadas podem ser reapresentadas.');
  return {
    ...state,
    invoices: state.invoices.map((item) =>
      item.id === id
        ? {
            ...item,
            status: 'pending',
            reason: undefined,
            history: [
              ...item.history,
              {
                at: stamp(),
                actor: 'Transportador',
                message: 'Fatura reapresentada para análise após revisão.',
              },
            ],
          }
        : item
    ),
  };
}
export function deliverInvoice(state: DemoState, id: string): DemoState {
  const invoice = state.invoices.find((item) => item.id === id);
  if (!invoice || invoice.status !== 'approved' || !invoice.protocol)
    throw new Error(
      'Apenas faturas aprovadas e com protocolo podem ser encaminhadas.'
    );
  if (state.deliveries.some((delivery) => delivery.invoiceId === id))
    throw new Error('O encaminhamento desta fatura já foi registrado.');
  return {
    ...state,
    deliveries: [
      ...state.deliveries,
      {
        id: `ENV-${state.deliveries.length + 1}`,
        invoiceId: id,
        at: stamp(),
        recipients: RECIPIENTS,
        protocol: invoice.protocol,
        status: 'Registrado localmente',
      },
    ],
    invoices: state.invoices.map((item) =>
      item.id === id
        ? {
            ...item,
            history: [
              ...item.history,
              {
                at: stamp(),
                actor: 'Financeiro',
                message:
                  'Encaminhamento registrado localmente. Prévia de e-mail não enviada.',
              },
            ],
          }
        : item
    ),
  };
}
export function validateTrip(
  state: DemoState,
  id: string,
  approve: boolean,
  reason = ''
): DemoState {
  const trip = state.trips.find((item) => item.id === id);
  if (
    !trip ||
    trip.validation !== 'pending' ||
    !metrics(trip).complete ||
    !metrics(trip).cents
  )
    throw new Error(
      'A viagem precisa estar encerrada, elegível e pendente de validação.'
    );
  if (!approve && !reason.trim())
    throw new Error('Informe o motivo da rejeição operacional.');
  return {
    ...state,
    trips: state.trips.map((item) =>
      item.id === id
        ? {
            ...item,
            validation: approve ? 'validated' : 'rejected',
            validationReason: approve
              ? 'Conferência operacional aprovada.'
              : reason.trim(),
          }
        : item
    ),
  };
}
export function exportCsv(
  filename: string,
  rows: (string | number)[][],
  trips?: Trip[]
) {
  const fields: Record<string, string> = {
    Local: 'Unidade',
    Unidade: 'Unidade',
    Categoria: 'Categoria de produto',
    'Peso TON': 'Tonelagem',
    'Classificação NF': 'Tipo de local',
    'CNPJ NF': 'CNPJ e endereço da NF',
    'Logradouro NF': 'CNPJ e endereço da NF',
  };
  const indexes =
    rows[0]
      ?.map((header, index) =>
        !trips ||
        !fields[header] ||
        trips.some((trip) => hasTripData(trip, fields[header]))
          ? index
          : -1
      )
      .filter((index) => index >= 0) || [];
  const visibleRows = rows.map((row) => indexes.map((index) => row[index]));
  const escape = (cell: string | number) => {
    let value = String(cell);
    if (/^[=+@-]/.test(value)) value = `'${value}`;
    return `"${value.replace(/"/g, '""')}"`;
  };
  const blob = new Blob(
    [
      '\uFEFF' +
        visibleRows.map((row) => row.map(escape).join(';')).join('\r\n'),
    ],
    { type: 'text/csv;charset=utf-8;' }
  );
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
